import {
  Link as MaterialLink,
  makeStyles,
  Paper,
  CircularProgress,
  Checkbox,
  Tooltip,
  IconButton,
  Button as MaterialButton,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import cn from 'classnames';
import { parse as parseQuery, stringify as stringifyQuery } from 'query-string';
import {
  useEffect,
  useState,
  SyntheticEvent,
  useMemo,
  useCallback,
} from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { useHistory, useLocation, useParams } from 'react-router';
import { Column, Table } from 'react-virtualized';
import _pick from 'lodash/pick';
import _uniq from 'lodash/uniq';
import { Founder } from '../../api/founders';
import gatheringsAPI, { Gathering } from '../../api/gatherings';
import {
  Text,
  AlertState,
  Link,
  Button,
  ConfirmButton,
  FormButtons,
  StickyContent,
  ModalV2,
} from '../../components/common';
import { getRoutePath, Pages } from '../../router/constants';
import {
  TableCell,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  TableFooter,
} from '../common/table';
import InviteGatheringFounders, {
  AssignFounders,
} from '../forms/invite-gathering-founders';

interface Props {
  loading: boolean;
  handleAssingFounders: (assigned: AssignFounders, callback: () => any) => any;
  handleRemoveFounders: (value: any, cb: () => any) => any;
}

interface InviteGatheringFoundersModalProps {
  loading: boolean;
  handleAssingFounders: (assigned: AssignFounders, callback: () => any) => any;
}

export interface TableValue {
  name: string;
  id: string;
  email: string;
}

const useStyles = makeStyles({
  container: {
    padding: 0,
    boxShadow: 'none',
  },
  filterContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 12px 0 16px',
    minHeight: 50,
    boxSizing: 'border-box',
    position: 'relative',
    zIndex: 2,
    flexWrap: 'wrap',
    rowGap: 15,
  },
  filterList: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    marginRight: 15,
    padding: '0 10px',
    justifyContent: 'flex-end',
  },
  filterItem: {
    '& + &': {
      marginLeft: 15,
    },
  },
  table: {
    outline: 'none',
  },
  column: {
    display: 'flex',
  },
  row: {
    display: 'flex',

    '&:hover $actionsCell': {
      display: 'flex',
    },
  },
  summary: {
    paddingRight: 20,
    boxSizing: 'border-box',
  },
  actionsCell: {
    display: 'none',
    alignItems: 'center',
  },
  dialog: {
    padding: '20px 32px 25px',
  },
  formButtons: {
    alignItems: 'flex-end',
    flexGrow: 1,
  },
  loadingBlock: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 696,
  },
  justifyContentEnd: {
    justifyContent: 'flex-end',
  },
  filterActions: {
    display: 'flex',
    alignItems: 'center',
    margin: '-1px 0',
    columnGap: 10,
  },
  emptyBlock: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 599,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: 500,
    rowGap: 15,
  },
  iconRemove: {
    marginRight: 25,
  },
  formButtonsFooter: {
    marginTop: 0,
  },
  stickyFooter: {
    padding: '0 !important',
    minHeight: '60px !important',
  },
});

async function gatheringsRequest(gatheringId: Gathering['id'], page: number) {
  const currentPage = page < 0 ? 0 : page;
  return await gatheringsAPI.getGatheringFounderList(gatheringId, currentPage);
}

const parseFounders = (value: Founder[]): TableValue[] => {
  if (value.length === 0) {
    return [];
  }

  return value.map((item) => ({
    name: `${item.firstName} ${item.lastName}`,
    id: item.id,
    email: item.email,
  }));
};

function getPageFromURL(search: string) {
  const parsedQuery = _pick(parseQuery(search), ['page']);
  return parsedQuery.page ? Number(parsedQuery.page) : 1;
}

const InviteGatheringFoundersModal = ({
  loading,
  handleAssingFounders,
}: InviteGatheringFoundersModalProps) => {
  const { gatheringId } = useParams<{ gatheringId: Gathering['id'] }>();

  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <ModalV2
        open={open}
        onClose={handleClose}
        titleProps={{
          title: 'Add a founder',
        }}
        width='720px'>
        <InviteGatheringFounders
          gatheringId={gatheringId}
          loading={loading}
          onSuccess={(value) => {
            handleAssingFounders(value, handleClose);
          }}
          onCancel={handleClose}
        />
      </ModalV2>
      <Button
        onClick={() => setOpen(true)}
        variant='contained'
        disabled={loading}>
        Add Founder
      </Button>
    </>
  );
};

const GatheringFoundersManually = ({
  loading,
  handleAssingFounders,
  handleRemoveFounders,
}: Props) => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const [founders, setFounders] = useState<TableValue[]>([]);
  const { gatheringId } = useParams<{ gatheringId: Gathering['id'] }>();
  const [isLoading, setIsLoading] = useState(true);
  const { width: containerWidth, ref: containerRef } = useResizeDetector();
  const [loadError, setLoadError] = useState();
  const [isEmptyState, setIsEmptyState] = useState(false);
  const [selectedFoundersIds, setSelectedFoundersIds] = useState<
    TableValue['id'][]
  >([]);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);

  const page = useMemo(
    () => getPageFromURL(location.search),
    [location.search],
  );
  const perPage = 10;

  const currentPath = useMemo(
    () => `${location.pathname}${location.search}`,
    [location],
  );

  const handleRowSelect = useCallback(
    (checked: boolean, founderId: TableValue['id']) => {
      if (checked) {
        return setSelectedFoundersIds(
          _uniq([...selectedFoundersIds, founderId]),
        );
      }
      return setSelectedFoundersIds(
        selectedFoundersIds.filter((id) => id !== founderId),
      );
    },
    [selectedFoundersIds],
  );

  const handleRowClick = useCallback(
    ({ rowData }) => {
      const founderId = rowData.id;
      handleRowSelect(!selectedFoundersIds.includes(founderId), founderId);
    },
    [selectedFoundersIds, handleRowSelect],
  );

  const handleAllRowSelect = useCallback(
    (checked: boolean) => {
      if (checked) {
        const foundersId = founders.map((founder) => founder.id);
        return setSelectedFoundersIds([...foundersId]);
      }
      return setSelectedFoundersIds([]);
    },
    [founders],
  );

  const stopPropagation = (e: SyntheticEvent<any>) => {
    e.stopPropagation();
  };

  const loadGatherings = async (currentPage: number) => {
    try {
      setFounders([]);
      setIsLoading(true);
      setIsEmptyState(false);

      const loadedGatherings = await gatheringsRequest(
        gatheringId,
        currentPage - 1,
      );

      if (loadedGatherings.length === 0) {
        throw new Error('404');
      }

      const resParse = parseFounders(loadedGatherings);
      setFounders(resParse);
      setIsLoading(false);

      if (loadedGatherings.length === 10) {
        setIsNextPageLoading(true);
        try {
          const nextGatherings = await gatheringsRequest(
            gatheringId,
            currentPage,
          );
          setIsNextPageLoading(false);
          setHasNextPage(nextGatherings.length > 0);
        } catch (e: any) {
          setIsNextPageLoading(false);
          setHasNextPage(false);
        }
      } else {
        setHasNextPage(false);
      }
    } catch (e: any) {
      if (e?.response?.status === 404 || e?.message === '404') {
        setIsLoading(false);
        return setIsEmptyState(true);
      }
      const errorMessage =
        e?.response?.data?.message || 'Internal server error';
      setIsLoading(false);
      setLoadError(errorMessage);
    }
  };

  const handlePageChange = useCallback(
    (nextPage) => {
      const query = stringifyQuery({
        page: nextPage > 1 ? nextPage : undefined,
      });

      if (query) {
        history.replace(`${location.pathname}?${query}`);
      } else {
        history.replace(location.pathname);
      }
    },
    [location.pathname, history],
  );

  const handleRemove = async (callback: () => any, founderIds: string[]) => {
    callback();
    await handleRemoveFounders(founderIds, () => loadGatherings(page - 1));
    setSelectedFoundersIds([]);
  };

  const handleReplication = async (cb: () => any) => {
    try {
      setIsLoading(true);
      await gatheringsAPI.foundersReplication(gatheringId);
      cb();
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadGatherings(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <Paper className={classes.container} data-testid='gathering-table'>
      <div className={classes.filterContainer}>
        <div className={classes.filterList}>
          {selectedFoundersIds.length > 0 && (
            <div className={classes.filterActions}>
              <Text variant='normal'>
                {selectedFoundersIds.length} selected
              </Text>
              <Tooltip title='Remove'>
                <ConfirmButton
                  title={`Remove the assignment${
                    selectedFoundersIds.length > 1 ? 's' : ''
                  }?`}
                  body={`Sure you want to remove the assignment${
                    selectedFoundersIds.length > 1 ? 's' : ''
                  }? Changes can't be undone`}
                  successProps={{
                    btnLabel: 'Remove',
                    onSuccess: (callback) =>
                      handleRemove(callback, selectedFoundersIds),
                  }}
                  cancelProps={{
                    btnLabel: 'Cancel',
                  }}
                  buttonRenderer={({ onClick }) => (
                    <IconButton onClick={onClick} disabled={loading}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                  loading={loading}
                />
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      <div ref={containerRef}>
        {founders?.length > 0 ? (
          <div>
            <Table
              onRowClick={handleRowClick}
              gridClassName={classes.table}
              headerHeight={56}
              height={64 * (founders.length > 10 ? founders.length : 10) + 56}
              rowHeight={64}
              rowCount={founders.length}
              rowGetter={({ index }) => founders[index]}
              rowClassName={classes.row}
              headerRowRenderer={(headRowProps) => (
                <TableHeadRow {...headRowProps} />
              )}
              rowRenderer={(rowProps) => <TableRow {...rowProps} />}
              width={containerWidth || 500}>
              <Column
                dataKey='id'
                className={classes.column}
                width={50}
                minWidth={50}
                headerRenderer={() => (
                  <Checkbox
                    checked={selectedFoundersIds.length === founders.length}
                    color='primary'
                    indeterminate={
                      selectedFoundersIds.length > 0 &&
                      selectedFoundersIds.length !== founders.length
                    }
                    onChange={(e) => handleAllRowSelect(e.target.checked)}
                  />
                )}
                cellRenderer={({ cellData }) => (
                  <Checkbox
                    data-testid='session-table-row-checkbox'
                    checked={selectedFoundersIds.includes(cellData)}
                    color='primary'
                    onChange={(e) =>
                      handleRowSelect(e.target.checked, cellData)
                    }
                  />
                )}
              />
              <Column
                dataKey='name'
                className={cn(classes.column, classes.summary)}
                headerClassName={classes.summary}
                width={100}
                minWidth={100}
                flexGrow={1}
                headerRenderer={() => (
                  <TableHeadCell>Founder Name</TableHeadCell>
                )}
                cellRenderer={({ cellData, rowData }) => (
                  <TableCell truncated>
                    <MaterialLink
                      data-testid='gathering-table-name'
                      onClick={stopPropagation}
                      component={Link}
                      to={{
                        pathname: getRoutePath(Pages.GATHERINGS_DETAILS, {
                          gatheringId: rowData.id,
                          groupId: rowData.groupId,
                        }),
                        state: {
                          prevPath: currentPath,
                        },
                      }}>
                      {cellData}
                    </MaterialLink>
                  </TableCell>
                )}
              />

              <Column
                dataKey='email'
                className={classes.column}
                width={200}
                minWidth={200}
                maxWidth={500}
                flexGrow={1}
                headerRenderer={() => <TableHeadCell>Email</TableHeadCell>}
                cellRenderer={({ cellData }) => {
                  return (
                    <TableCell truncated>
                      <Text variant='normal'>{cellData}</Text>
                    </TableCell>
                  );
                }}
              />
              <Column
                dataKey='actions'
                className={cn(classes.column, classes.justifyContentEnd)}
                minWidth={120}
                width={120}
                headerRenderer={() => <div></div>}
                cellRenderer={({ rowData }) => (
                  <TableCell onClick={stopPropagation}>
                    <div className={classes.actionsCell}>
                      <ConfirmButton
                        title='Remove the assignment?'
                        body='Sure you want to remove the assignment? Changes can’t be undone'
                        successProps={{
                          btnLabel: 'Remove',
                          onSuccess: (callback) =>
                            handleRemove(callback, [rowData.id]),
                        }}
                        cancelProps={{
                          btnLabel: 'Cancel',
                        }}
                        buttonRenderer={({ onClick }) => (
                          <Tooltip title='Remove'>
                            <IconButton
                              onClick={onClick}
                              disabled={loading}
                              className={classes.iconRemove}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        loading={loading}
                      />
                    </div>
                  </TableCell>
                )}
              />
            </Table>
          </div>
        ) : (
          <>
            {isEmptyState ? (
              <div className={classes.emptyBlock}>
                <div className={classes.emptyState}>
                  <Text variant='normal' testid='founder-table-no-items'>
                    Currently we have no founders invited to this gathering.
                    <br />
                    Please feel free to add new by clicking on a button below
                  </Text>
                  <InviteGatheringFoundersModal
                    loading={loading}
                    handleAssingFounders={handleAssingFounders}
                  />
                </div>
              </div>
            ) : (
              <div className={classes.loadingBlock}>
                {isLoading && <CircularProgress size={36} color='primary' />}
                {!isLoading && !!loadError && (
                  <AlertState type='error'>{loadError}</AlertState>
                )}
              </div>
            )}
          </>
        )}
        {!!perPage && (
          <StickyContent
            className={classes.stickyFooter}
            inaccuracy={36}
            isShow={founders?.length > 0}>
            <TableFooter
              page={page}
              onPageChange={handlePageChange}
              disabled={isLoading}
              isLoading={isNextPageLoading}
              hasNextPage={founders.length > 0 && hasNextPage}>
              {founders?.length > 0 && (
                <FormButtons className={classes.formButtonsFooter}>
                  <InviteGatheringFoundersModal
                    loading={loading}
                    handleAssingFounders={handleAssingFounders}
                  />
                  <ConfirmButton
                    title='Replicate'
                    body='We will replicate this instance founders subscription list to the whole gathering group. Do you want to proceed?'
                    successProps={{
                      btnLabel: 'Apply',
                      onSuccess: handleReplication,
                    }}
                    cancelProps={{
                      btnLabel: 'Cancel',
                    }}
                    loading={isLoading}
                    buttonRenderer={({ onClick }) => (
                      <Tooltip title='All founders that currently assigned to the current gathering instance will be assigned to the whole gathering group'>
                        <MaterialButton
                          onClick={onClick}
                          variant='contained'
                          color='primary'
                          disabled={isLoading}>
                          Replicate Invitees
                        </MaterialButton>
                      </Tooltip>
                    )}
                  />
                </FormButtons>
              )}
            </TableFooter>
          </StickyContent>
        )}
      </div>
    </Paper>
  );
};

export default GatheringFoundersManually;
